import { createContext, JSX, ReactNode, useContext, useMemo, useState } from 'react';
import { downloadCSV } from 'react-admin';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

/**
 * A node of the Markdown tree react-markdown hands to a component override.
 *
 * Only the parts a table is made of are described. The alternative was walking the rendered React
 * children, which means reaching into `props.children` of elements this file did not create and
 * unpicking arrays, strings and nested overrides. The tree is the same data before any of that
 * happened, so it is read instead.
 */
interface MarkdownNode {
    type: string;
    tagName?: string;
    value?: string;
    children?: MarkdownNode[];
}

/**
 * Whether the message holding this table is still being written.
 *
 * A table arrives a row at a time, so exporting one mid-stream downloads however much of it had
 * appeared - which looks like data loss rather than an interrupted download. The flag comes
 * through a context because `components` on the Markdown element stays a literal of component
 * references that way, rather than a fresh object built per delta.
 */
export const ChatStreamingContext = createContext<boolean>(false);

/** Everything a cell says, with its links flattened to their labels. */
const cellText: (node: MarkdownNode) => string = (node: MarkdownNode): string => {
    if (node.type === 'text') {
        return node.value ?? '';
    }
    if (!node.children) {
        return '';
    }
    return node.children.map(cellText).join('');
};

/** Every row of a table, header first, as plain strings. */
const tableRows: (node: MarkdownNode | undefined) => string[][] =
    (node: MarkdownNode | undefined): string[][] => {
        if (!node || !node.children) {
            return [];
        }

        // Rows normally sit inside a thead or tbody, but the HTML parser is permissive and a
        // hand-written table can put a tr straight under the table, so both are collected.
        const rowNodes: MarkdownNode[] = [];
        for (const child of node.children) {
            if (child.tagName === 'tr') {
                rowNodes.push(child);
            } else if (child.children && ['thead', 'tbody', 'tfoot'].includes(child.tagName ?? '')) {
                rowNodes.push(...child.children.filter(
                    (row: MarkdownNode): boolean => row.tagName === 'tr'
                ));
            }
        }

        return rowNodes.map((row: MarkdownNode): string[] =>
            (row.children ?? [])
                .filter((cell: MarkdownNode): boolean => cell.tagName === 'th' || cell.tagName === 'td')
                .map((cell: MarkdownNode): string => cellText(cell).trim())
        );
    };

/**
 * Excel reads a UTF-8 file as the local code page unless it starts with this, so an address
 * with an accent in it arrives mangled. `downloadCSV` does not add one. Written as an escape
 * rather than the character itself, which is invisible in an editor and survives a careless
 * copy and paste only by luck.
 */
const BYTE_ORDER_MARK: string = '\ufeff';

/**
 * One CSV field.
 *
 * Company names contain commas and addresses contain newlines, so this is the part that has to be
 * right: a value carrying a comma, a quote or a line break is quoted, and quotes inside it are
 * doubled, per RFC 4180.
 */
const csvField: (value: string) => string = (value: string): string =>
    /["\n\r,]/.test(value) ? '"' + value.replace(/"/g, '""') + '"' : value;

const toCsv: (rows: string[][]) => string = (rows: string[][]): string =>
    rows.map((row: string[]): string => row.map(csvField).join(',')).join('\r\n');

/** A file name from the table's first heading, so two downloads do not overwrite each other. */
const fileName: (rows: string[][]) => string = (rows: string[][]): string => {
    const heading: string = (rows[0]?.[0] ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const today: string = new Date().toISOString().slice(0, 10);
    return (heading === '' ? 'chat-table' : heading) + '-' + today;
};

/**
 * A table in a reply, with a control to take it away as CSV.
 *
 * The download is what the assistant actually printed, not the records behind it. That is the
 * point: a table is often assembled from several lookups and trimmed to the columns that answer
 * the question, and a file that disagrees with the table above it would be worse than no file.
 */
export const ChatTable: (props: { node?: MarkdownNode, children?: ReactNode }) => JSX.Element =
    ({ node, children }): JSX.Element => {
        const streaming: boolean = useContext(ChatStreamingContext);
        const [saved, setSaved] = useState<boolean>(false);
        const rows: string[][] = useMemo((): string[][] => tableRows(node), [node]);

        // One row is a header with nothing under it, which is not worth offering.
        const exportable: boolean = !streaming && rows.length > 1;

        function handleExport(): void {
            downloadCSV(BYTE_ORDER_MARK + toCsv(rows), fileName(rows));
            setSaved(true);
            setTimeout((): void => setSaved(false), 1500);
        }

        return (
            <div className="message__table">
                { exportable && (
                    <button
                        type="button"
                        className="table__export"
                        onClick={ handleExport }
                        title="Download this table as CSV"
                    >
                        <FileDownloadOutlinedIcon fontSize="inherit" />
                        { saved ? 'Saved' : 'CSV' }
                    </button>
                ) }
                <table>{ children }</table>
            </div>
        );
    };
